/**
 * Integration Service API Client
 * Handles communication with the Integration Service for F&O data
 * 
 * NOTE: Uses centralized apiClient for automatic 401 retry and token refresh
 */

import { apiClient } from '../apiClient'

export interface EmployeeData {
  // Core Identification
  personnel_number: string
  party_number: string
  full_name: string
  first_name?: string
  middle_name?: string
  last_name?: string
  last_name_prefix?: string
  known_as?: string
  name_alias?: string
  personal_title?: string
  personal_suffix?: string
  professional_title?: string
  professional_suffix?: string
  
  // Contact Information
  primary_contact_email?: string
  primary_contact_email_description?: string
  primary_contact_email_purpose?: string
  primary_contact_phone?: string
  primary_contact_phone_description?: string
  primary_contact_phone_extension?: string
  primary_contact_phone_is_mobile?: string
  primary_contact_phone_purpose?: string
  primary_contact_fax?: string
  primary_contact_url?: string
  primary_contact_linkedin?: string
  primary_contact_facebook?: string
  primary_contact_twitter?: string
  
  // Employment Details
  worker_status?: string
  worker_type?: string
  title_id?: string
  office_location?: string
  office_location_id?: string
  original_hire_date?: string
  anniversary_date?: string
  seniority_date?: string
  allow_rehire?: string
  works_from_home?: string

  // EmploymentsV2 enrichment
  employment_campus?: string
  employment_campus_name?: string
  employment_faculty?: string
  employment_faculty_name?: string
  employment_hed_grade_level_id?: string
  employment_hcm_nqf_level?: string
  employment_hcm_nqf_level_description?: string
  employment_high_potential?: string
  employment_worker_type?: string
  employment_start_date?: string
  employment_end_date?: string

  // Position/Job enrichment
  position_id?: string
  position_description?: string
  position_department_number?: string
  position_department_name?: string
  position_job_id?: string
  position_job_title?: string
  position_job_family_id?: string
  position_function_id?: string
  position_type_id?: string
  position_reports_to_position_id?: string

  // Personal Details
  gender?: string
  birth_date?: string
  person_birth_city?: string
  person_birth_country_region?: string
  marital_status?: string
  ethnic_origin_id?: string
  citizenship_country_region?: string
  nationality_country_region?: string
  number_of_dependents?: number
  education?: string
  native_language_id?: string
  is_disabled?: string
  is_disabled_veteran?: string
  veteran_status_id?: string
  is_fulltime_student?: string
  deceased_date?: string
  
  // Address
  address_street?: string
  address_city?: string
  address_state?: string
  address_zip_code?: string
  address_county?: string
  address_district_name?: string
  address_country_region_id?: string
  address_country_region_iso_code?: string
  address_purpose?: string
  address_books?: string
  address_valid_from?: string
  address_valid_to?: string
  
  // Sage Integration
  sage_integrated?: string
  sage_id?: string
  sage_company_rule_code?: string
  sage_nature_of_contract_code?: string
  sage_email_basic_payslip_code?: string
  
  // Identity & System
  language_id?: string
  party_type?: string
  object_id?: string
  user?: string
  identity_email?: string
  identity_provider?: string
  
  // Military Service
  military_service_start_date?: string
  military_service_end_date?: string
  
  // Parent Birth Countries
  father_birth_country_region?: string
  mother_birth_country_region?: string
  
  // Expatriate
  is_expatriate_ruling_applicable?: string
  expatriate_ruling_valid_from?: string
  expatriate_ruling_valid_to?: string
}

export interface PositionData {
  position_id: string
  position_description: string
  job_id?: string
  title_id?: string
  department_number?: string
  worker_name?: string
  worker_personnel_number?: string
  detail_effective?: string
  detail_expiration?: string
  activation?: string
  retirement?: string
  is_primary_position?: string
  full_time_equivalent?: number
  reports_to_position_id?: string
}

export interface JobDefinition {
  job_id: string
  title_id: string
  description?: string
  job_description?: string
  job_type_id?: string
  job_family_id?: string
  function_id?: string
  full_time_equivalent?: number
  effective?: string
  expiration?: string
}

export interface TestPersona {
  id: string
  personnelNumber: string
  email: string
  fullName: string
  firstName: string
  lastName: string
  jobTitle: string
  department: string
  campus: string
  campusName: string
  functionalArea: string
  jobFamilyId?: string
  roles: string[]
  positionId: string
  positionDescription: string
  managerPersonnelNumber?: string
  reportsToPositionId?: string
  isPrimary: boolean
}

// Historical Performance Data from F&O
export interface RatingLevel {
  rating_id: string
  rating_model: string
  description: string
  factor: number
}

export interface HistoricalPerformanceReview {
  discussion_id: string
  personnel_number: string
  description: string
  period_id: string
  year: number
  review_type: 'mid-year' | 'year-end'
  final_rating_id: string | null
  final_rating_description: string | null
  manager_rating_id: string | null
  manager_rating_description: string | null
  approval_status: string
  comments: string | null
  link_to_discussion: string | null
}

export interface HistoricalPerformanceResponse {
  personnel_number: string
  employee_name: string | null
  reviews: HistoricalPerformanceReview[]
  rating_scale: RatingLevel[]
}

// Identity service user response type
interface IdentityUserResponse {
  id: string
  email: string
  first_name?: string
  last_name?: string
  display_name: string
  employee_id?: string
  department?: string
  job_title?: string
  campus?: string
}

class IntegrationService {

  /**
   * Get employee display name - prefers identity service (accurate), falls back to F&O
   * This handles sandbox data mismatches gracefully where F&O data may be stale
   */
  async getEmployeeDisplayName(personnelNumber: string): Promise<{ full_name: string; source: 'identity' | 'fo' | 'fallback' }> {
    // Try identity service first (accurate, synced from Azure AD)
    try {
      const response = await apiClient.get<IdentityUserResponse>(
        `/api/v1/identity/users/by-employee-id/${personnelNumber}`
      )
      if (response.data?.display_name) {
        return { full_name: response.data.display_name, source: 'identity' }
      }
    } catch {
      // Identity service doesn't have this user - try F&O
      if (import.meta.env.DEV) console.log(`Identity lookup failed for ${personnelNumber}, trying F&O`)
    }

    // Fallback to F&O (may have stale sandbox data)
    try {
      const response = await apiClient.get<EmployeeData>(
        `/integration/f0/employees/${personnelNumber}`
      )
      if (response.data?.full_name) {
        return { full_name: response.data.full_name, source: 'fo' }
      }
    } catch {
      if (import.meta.env.DEV) console.log(`F&O lookup also failed for ${personnelNumber}`)
    }

    // Final fallback - return personnel number as name
    return { full_name: personnelNumber, source: 'fallback' }
  }

  /**
   * Get employee by personnel number (F&O only - use getEmployeeDisplayName for names)
   */
  async getEmployeeByPersonnelNumber(personnelNumber: string): Promise<EmployeeData> {
    try {
      const response = await apiClient.get<EmployeeData>(
        `/integration/f0/employees/${personnelNumber}`
      )
      return response.data
    } catch (error) {
      console.error(`Failed to fetch employee ${personnelNumber}:`, error)
      throw error
    }
  }

  /**
   * Get position by position ID
   */
  async getPositionById(positionId: string): Promise<PositionData> {
    try {
      const response = await apiClient.get<PositionData>(
        `/integration/f0/positions/${positionId}`
      )
      return response.data
    } catch (error) {
      console.error(`Failed to fetch position ${positionId}:`, error)
      throw error
    }
  }

  /**
   * Get all positions from F&O
   * Used for career aspiration role selection
   */
  async getAllPositions(params?: {
    skip?: number
    top?: number
  }): Promise<{ positions: PositionData[]; total_count: number }> {
    try {
      const response = await apiClient.get<{ positions: PositionData[]; total_count: number }>(`/api/v1/integration/f0/positions/`, { 
        params: { skip: params?.skip || 0, top: params?.top || 500 }
      })
      return response.data
    } catch (error) {
      console.error('Failed to fetch positions:', error)
      throw error
    }
  }

  /**
   * Get jobs from F&O with optional search
   * Jobs represent role types (e.g., "Senior Lecturer", "Department Head")
   * Used for career aspiration target role selection
   */
  async getJobs(params?: {
    skip?: number
    top?: number
    search?: string
  }): Promise<{ jobs: JobDefinition[]; total_count: number }> {
    try {
      const response = await apiClient.get<{ jobs: JobDefinition[]; total_count: number }>(`/api/v1/integration/f0/jobs/`, { 
        params: { 
          skip: params?.skip || 0, 
          top: params?.top || 50,
          search: params?.search || undefined
        }
      })
      return response.data
    } catch (error) {
      console.error('Failed to fetch jobs:', error)
      throw error
    }
  }

  /**
   * Search jobs by title or description (uses backend search)
   */
  async searchJobs(query: string, limit: number = 20): Promise<JobDefinition[]> {
    try {
      const { jobs } = await this.getJobs({ search: query, top: limit })
      return jobs
    } catch (error) {
      console.error('Failed to search jobs:', error)
      throw error
    }
  }

  /**
   * Search employees from cached data (fast, no F&O API hit).
   *
   * Backed by the `users` table which the daily F&O sync (and the
   * new-hire saga's `_upsert_user_from_saga` step) keeps in shape.
   * `worker_status` reflects F&O `Worker.WorkerStatus` ('Employed' /
   * 'Terminated' / etc.) rather than a derived is_active boolean.
   */
  async searchEmployees(
    query: string,
    limit: number = 20,
    filters?: {
      status?: 'active' | 'terminated' | 'all'
      campus?: string
      faculty?: string
      positionType?: string
      // Include HR-only directory rows (F&O workers without an Azure AD
      // account — cleaners on shared logins, casual workers, terminated
      // employees retained for history). Default false. HR Ops Find &
      // Amend passes true so it sees every F&O worker.
      includeHrOnly?: boolean
    },
  ): Promise<{ employees: EmployeeData[]; total_count: number }> {
    try {
      const params: Record<string, string | number | boolean> = { query, limit }
      if (filters?.status) params.status = filters.status
      if (filters?.campus) params.campus = filters.campus
      if (filters?.faculty) params.faculty = filters.faculty
      if (filters?.positionType) params.position_type = filters.positionType
      if (filters?.includeHrOnly) params.include_hr_only = true
      const response = await apiClient.get<{ employees: EmployeeData[]; total_count: number }>(`/integration/employees/search`, {
        params,
      })
      return response.data
    } catch (error) {
      console.error('Failed to search employees:', error)
      throw error
    }
  }

  /**
   * Distinct filter values for the Find & Amend ledger dropdowns.
   * Pulls from the platform Users table (same source as searchEmployees).
   *
   * `includeHrOnly` mirrors the same flag on `searchEmployees` — Find &
   * Amend passes true so the chips include campuses/faculties present
   * only on HR-only directory rows. Default false matches the search
   * default for portal-facing callers.
   */
  async getEmployeeSearchFacets(includeHrOnly: boolean = false): Promise<{
    campuses: string[]
    faculties: Array<{ code: string; name: string }>
    position_types: string[]
  }> {
    try {
      const params: Record<string, boolean> = {}
      if (includeHrOnly) params.include_hr_only = true
      const response = await apiClient.get<{
        campuses: string[]
        faculties: Array<{ code: string; name: string }>
        position_types: string[]
      }>(`/integration/employees/search/facets`, { params })
      return response.data
    } catch (error) {
      console.error('Failed to fetch employee search facets:', error)
      throw error
    }
  }

  /**
   * Sync employees from F&O to cache
   */
  async syncEmployeesCache(): Promise<{ status: string; synced_count: number; synced_at: string }> {
    try {
      const response = await apiClient.post<{ status: string; synced_count: number; synced_at: string }>(`/integration/employees/sync`, null)
      return response.data
    } catch (error) {
      console.error('Failed to sync employees:', error)
      throw error
    }
  }

  /**
   * Get cache status
   */
  async getEmployeeCacheStatus(): Promise<{
    total_employees: number
    last_synced: string | null
    status_breakdown: Record<string, number>
    is_stale: boolean
  }> {
    try {
      const response = await apiClient.get<{
        total_employees: number
        last_synced: string | null
        status_breakdown: Record<string, number>
        is_stale: boolean
      }>(`/integration/employees/cache/status`)
      return response.data
    } catch (error) {
      console.error('Failed to get cache status:', error)
      throw error
    }
  }

  /**
   * Get all employees from F&O (real data from sandbox)
   */
  async getAllEmployees(params?: {
    skip?: number
    top?: number
    search?: string
    department?: string
    campus?: string
  }): Promise<{ employees: EmployeeData[]; total_count: number }> {
    try {
      // Add trailing slash to prevent 307 redirect
      const response = await apiClient.get<{ employees: EmployeeData[]; total_count: number }>(`/integration/f0/employees/`, { 
        params
      })
      return response.data
    } catch (error) {
      console.error('Failed to fetch F&O employees:', error)
      throw error
    }
  }

  /**
   * Get test personas (development mode only)
   */
  async getTestPersonas(): Promise<{ personas: TestPersona[]; total: number }> {
    try {
      const response = await apiClient.get<{ personas: TestPersona[]; total: number }>(`/integration/test-users/personas`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch test personas:', error)
      throw error
    }
  }

  /**
   * Get test persona details (development mode only)
   */
  async getTestPersonaDetails(personnelNumber: string): Promise<{
    persona: any
    foData: { employee: EmployeeData; position: PositionData }
  }> {
    try {
      const response = await apiClient.get<{
        persona: any
        foData: { employee: EmployeeData; position: PositionData }
      }>(
        `/integration/test-users/personas/${personnelNumber}`
      )
      return response.data
    } catch (error) {
      console.error(`Failed to fetch persona details for ${personnelNumber}:`, error)
      throw error
    }
  }

  /**
   * Get manager details for an employee
   * Fetches the manager's information using the manager personnel number
   */
  async getManagerDetails(managerPersonnelNumber: string): Promise<EmployeeData | null> {
    try {
      return await this.getEmployeeByPersonnelNumber(managerPersonnelNumber)
    } catch (error: any) {
      // Return null if manager not found (404) or other errors
      if (error.response?.status === 404) {
        console.warn(`Manager with personnel number ${managerPersonnelNumber} not found`)
        return null
      }
      console.error(`Failed to fetch manager ${managerPersonnelNumber}:`, error)
      return null
    }
  }

  // ==================== UAT PERSONA ENDPOINTS ====================

  /**
   * Get UAT participants list
   */
  async getUATParticipants(): Promise<{
    participants: Array<{ id: string; name: string; email: string }>
    total_count: number
    summary: any
  }> {
    try {
      const response = await apiClient.get<{
        participants: Array<{ id: string; name: string; email: string }>
        total_count: number
        summary: any
      }>(`/api/v1/integration/uat/participants`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch UAT participants:', error)
      throw error
    }
  }

  /**
   * Get UAT personas for a specific participant by email
   */
  async getUATPersonasForParticipant(email: string): Promise<{
    participant: { id: string; name: string; email: string }
    personas: {
      real: { type: string; available: boolean }
      employee: {
        type: string
        personnel_number: string
        name: string
        job_title: string
        department: string
        campus: string
        functional_area: string
        available: boolean
      }
      manager: {
        type: string
        personnel_number: string
        name: string
        job_title: string
        department: string
        campus: string
        functional_area: string
        direct_reports: string[]
        available: boolean
      }
    }
  }> {
    try {
      const response = await apiClient.get<{
        participant: { id: string; name: string; email: string }
        personas: {
          real: { type: string; available: boolean }
          employee: {
            type: string
            personnel_number: string
            name: string
            job_title: string
            department: string
            campus: string
            functional_area: string
            available: boolean
          }
          manager: {
            type: string
            personnel_number: string
            name: string
            job_title: string
            department: string
            campus: string
            functional_area: string
            direct_reports: string[]
            available: boolean
          }
        }
      }>(
        `/api/v1/integration/uat/participants/${encodeURIComponent(email)}/personas`
      )
      return response.data
    } catch (error) {
      console.error(`Failed to fetch UAT personas for ${email}:`, error)
      throw error
    }
  }

  /**
   * Seed UAT personas into the database
   */
  async seedUATPersonas(participantEmail?: string): Promise<{
    status: string
    created: number
    updated: number
    manager_relationships_mapped: number
    total_processed: number
    errors?: Array<{ persona: string; error?: string; relationship_error?: string }> | null
  }> {
    try {
      const url = participantEmail
        ? `/api/v1/integration/uat/personas/seed?participant_email=${encodeURIComponent(participantEmail)}`
        : `/api/v1/integration/uat/personas/seed`
      const response = await apiClient.post<{
        status: string
        created: number
        updated: number
        manager_relationships_mapped: number
        total_processed: number
        errors?: Array<{ persona: string; error?: string; relationship_error?: string }> | null
      }>(url, null)
      return response.data
    } catch (error) {
      console.error('Failed to seed UAT personas:', error)
      throw error
    }
  }

  /**
   * Get all UAT employee personas
   */
  async getUATEmployeePersonas(): Promise<{
    personas: Array<{
      personnel_number: string
      name: string
      job_title: string
      department: string
      campus: string
      functional_area: string
      uat_owner: string
    }>
    count: number
  }> {
    try {
      const response = await apiClient.get<{
        personas: Array<{
          personnel_number: string
          name: string
          job_title: string
          department: string
          campus: string
          functional_area: string
          uat_owner: string
        }>
        count: number
      }>(`/api/v1/integration/uat/personas/employee`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch UAT employee personas:', error)
      throw error
    }
  }

  /**
   * Get all UAT manager personas
   */
  async getUATManagerPersonas(): Promise<{
    personas: Array<{
      personnel_number: string
      name: string
      job_title: string
      department: string
      campus: string
      functional_area: string
      uat_owner: string
      direct_reports: string[]
    }>
    count: number
  }> {
    try {
      const response = await apiClient.get<{
        personas: Array<{
          personnel_number: string
          name: string
          job_title: string
          department: string
          campus: string
          functional_area: string
          uat_owner: string
          direct_reports: string[]
        }>
        count: number
      }>(`/api/v1/integration/uat/personas/manager`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch UAT manager personas:', error)
      throw error
    }
  }

  // ==================== END UAT PERSONA ENDPOINTS ====================

  // ==================== HISTORICAL PERFORMANCE (F&O) ====================

  /**
   * Get historical performance reviews from F&O for an employee
   * @param personnelNumber - Employee personnel number (e.g., "43xxxxx")
   * @param year - Optional year filter
   */
  async getHistoricalReviews(
    personnelNumber: string,
    year?: number
  ): Promise<HistoricalPerformanceResponse> {
    try {
      const params = year ? { year } : {}
      const response = await apiClient.get<HistoricalPerformanceResponse>(
        `/api/v1/integration/historical/reviews/${personnelNumber}`,
        { params }
      )
      return response.data
    } catch (error) {
      // Return empty response when historical data is unavailable (e.g. no F&O connection, no data for employee)
      if (import.meta.env.DEV) console.debug(`No historical reviews available for ${personnelNumber}${year ? ` (${year})` : ''}`)
      return {
        personnel_number: personnelNumber,
        employee_name: null,
        reviews: [],
        rating_scale: []
      }
    }
  }

  /**
   * Get all rating levels from F&O
   * @param ratingModel - Optional filter by rating model (e.g., "RM 2024")
   */
  async getRatingLevels(ratingModel?: string): Promise<RatingLevel[]> {
    try {
      const params = ratingModel ? { rating_model: ratingModel } : {}
      const response = await apiClient.get<RatingLevel[]>(
        `/api/v1/integration/historical/rating-levels`,
        { params }
      )
      return response.data
    } catch (error) {
      console.error('Failed to fetch rating levels:', error)
      throw error
    }
  }

  // ==================== END HISTORICAL PERFORMANCE ====================

  /**
   * Get employee context including manager information
   * This is useful for populating employee context state in components
   */
  async getEmployeeContext(personnelNumber: string): Promise<{
    employee: EmployeeData
    manager: EmployeeData | null
    position: PositionData | null
  }> {
    try {
      // In development mode with test users, use test persona data
      if (import.meta.env.MODE === 'development' && personnelNumber.startsWith('TEST')) {
        const personaDetails = await this.getTestPersonaDetails(personnelNumber)
        
        // Get manager details if manager personnel number exists
        let manager: EmployeeData | null = null
        if (personaDetails.persona.manager_personnel_number) {
          const managerPersona = await this.getTestPersonaDetails(
            personaDetails.persona.manager_personnel_number
          )
          manager = managerPersona.foData.employee
        }

        return {
          employee: personaDetails.foData.employee,
          manager,
          position: personaDetails.foData.position
        }
      }

      // Production mode: fetch from real F&O endpoints
      const employee = await this.getEmployeeByPersonnelNumber(personnelNumber)
      
      // Fetch manager if available
      // Note: We need to get position first to get manager's personnel number
      // This is a simplified approach - adjust based on your F&O data structure
      const manager = null // TODO: Implement based on F&O position hierarchy
      
      return {
        employee,
        manager,
        position: null
      }
    } catch (error) {
      console.error(`Failed to fetch employee context for ${personnelNumber}:`, error)
      throw error
    }
  }
}

export const integrationService = new IntegrationService()
